import { useRefsStore } from '~/composables/refsStore';
import type { AsyncData, AsyncDataOptions } from 'nuxt/app';
import { hash } from 'ohash';
import {
  stegaClean,
  type SanityClient,
  type ClientPerspective,
  type ContentSourceMap,
  type QueryParams,
} from '@sanity/client/stega';
import type { Ref } from 'vue';
import {
  defineEncodeDataAttribute,
  type EncodeDataAttributeFunction,
} from '@sanity/core-loader/encode-data-attribute';
import type { QueryStore, QueryStoreState } from '@sanity/core-loader';
import * as AssertThat from '~/utils/AssertThat';

interface BaseInterface<T> {
  data: Ref<T>;
  sourceMap: Ref<ContentSourceMap | null>;
  encodeDataAttribute: Ref<() => void>;
}

// Types from @nuxtjs/sanity
interface AsyncSanityData<T> extends Promise<BaseInterface<T>> {
  data: Ref<T>;
  sourceMap: Ref<ContentSourceMap | null>;
  encodeDataAttribute: Ref<() => void>;
}

interface SanityHelperConfiguration {
  visualEditing?: {
    studioUrl?: string;
    proxyEndpoint?: string;
  };
}

interface UseSanityQueryOptions<T> extends AsyncDataOptions<T> {
  client?: string;
}

interface SanityQueryResponse<T> {
  data: T;
  sourceMap?: ContentSourceMap;
  perspective?: ClientPerspective;
}

type Noop = () => void;

export type SanityHelper = ReturnType<typeof useSanity> & {
  queryStore?: QueryStore;
  config: SanityHelperConfiguration;
};

const pushDebug = ({
  query,
  params,
  queryKey,
}: {
  query: string;
  params: QueryParams;
  queryKey: string;
}) => {
  try {
    if (!window) return;
    window.ptcDebug = window.ptcDebug || {};
    window.ptcDebug.clientQuery = window.ptcDebug.clientQuery || {};
    window.ptcDebug.clientQuery[queryKey] = {
      query,
      params,
    };
    console.warn('Pushing to ptcDebug', {
      queryKey,
      query,
      params,
    });
  } catch (e) {
    console.warn('Error pushing to ptcDebug', e);
  }
};

const pushClientErr = ({
  query,
  params,
  queryKey,
  msg,
  debugPath,
}: {
  query: string;
  params: QueryParams;
  queryKey: string;
  msg: string;
  debugPath: Array<string>;
}) => {
  try {
    console.warn(
      'Pushing to ptcDebug',
      JSON.stringify(
        {
          queryKey,
          query,
          params,
          debugPath,
          trace: new Error().stack,
        },
        null,
        2
      )
    );
    if (!window) return;
    window.ptcDebug = window.ptcDebug || {};
    window.ptcDebug.errs = window.ptcDebug.errs || [];
    window.ptcDebug.errs.push({
      msg,
      query,
      params: JSON.parse(JSON.stringify(params)),
      queryKey,
      debugPath: debugPath || [],
    });
  } catch (e) {
    console.warn('Error pushing to ptcDebug', e);
  }
};

type WwwExtraOptions = {
  perspective?: string;
  /** used for debugging */
  wwwHint?: string;
};

export const useWwwSanityQuery = <T = unknown, E = Error>(
  query: string,
  _params?: QueryParams,
  _options: UseSanityQueryOptions<SanityQueryResponse<T | null>> & WwwExtraOptions = {}
): AsyncSanityData<T | null> => {
  const { client, perspective: _perspective, ...options } = _options;
  const sanity = useSanity(client) as SanityHelper;
  const params = _params ? reactive(_params) : undefined;
  // default currentPathname parameter to start with /en/ if it exists
  if (params && params.currentPathname) {
    params.currentPathname = localizePathname(params.currentPathname);
  }
  if (params) {
    options.watch = options.watch || [];
    options.watch.push(params);
  }

  let perspective = 'published' as ClientPerspective;
  if (_perspective || sanity.queryStore) {
    perspective = 'previewDrafts';
  }

  const keyHint = options.wwwHint && options.wwwHint.length > 0 ? `${_options.wwwHint}-` : '';

  const queryKey = 'sanity-' + keyHint + hash(query + (params ? JSON.stringify(params) : ''));

  const data = ref<T | null>(null) as Ref<T | null>; // Generic must be cast
  const sourceMap = ref<ContentSourceMap | null>(null);
  const encodeDataAttribute = ref<EncodeDataAttributeFunction | Noop>(() => {});
  const updateRefs = (newData: T | null, newSourceMap?: ContentSourceMap) => {
    data.value = newData;
    sourceMap.value = newSourceMap || null;
    encodeDataAttribute.value = defineEncodeDataAttribute(
      newData,
      newSourceMap,
      sanity.config.visualEditing?.studioUrl
    );
  };

  let result: AsyncData<SanityQueryResponse<T | null>, E>;

  const debugPath: Array<string> = [];

  if (!sanity.queryStore) {
    debugPath.push('!sanity.queryStore');
    const { fetch } = useCacheFetchSanity();
    result = useAsyncData<SanityQueryResponse<T | null>, E>(
      queryKey,
      async () => {
        pushDebug({ query, params: params || {}, queryKey });
        const data = stegaClean(
          await fetch<T>({
            sanity,
            query,
            keyHint,
            params: params || {},
            options: { perspective },
          })
        ) as T | null;
        const { extractRefs, getSanityQueryAndParamsFromRefs, storeRefs } = useRefsStore();
        const firstExtractedRefs = extractRefs(data);
        if (!firstExtractedRefs.length) {
          return { data };
        }
        const { query: firstQuery, params: firstParams } =
          getSanityQueryAndParamsFromRefs(firstExtractedRefs);
        const firstResp = await fetch<T>({
          sanity,
          query: firstQuery,
          params: firstParams,
          keyHint,
        });
        storeRefs({
          refs: firstExtractedRefs,
          data: firstResp,
        });
        const secondExtractedRefs = extractRefs(firstResp);
        if (!secondExtractedRefs.length) {
          return { data };
        }
        const { query: secondQuery, params: secondParams } =
          getSanityQueryAndParamsFromRefs(secondExtractedRefs);
        const secondResp = await fetch<T>({
          sanity,
          query: secondQuery,
          params: secondParams,
          keyHint,
        });
        storeRefs({
          refs: secondExtractedRefs,
          data: secondResp,
        });
        return { data };
      },
      options
    ) as AsyncData<SanityQueryResponse<T | null>, E>;
  } else {
    debugPath.push('sanity.queryStore');
    let unsubscribe = () => {};

    const setupFetcher = (cb?: (_state: Readonly<QueryStoreState<T, E>>) => void) => {
      unsubscribe();

      const fetcher = sanity.queryStore!.createFetcherStore<T, E>(
        query,
        params ? JSON.parse(JSON.stringify(params)) : undefined,
        undefined
      );

      unsubscribe = fetcher.subscribe((newSnapshot) => {
        if (newSnapshot.data) {
          updateRefs(newSnapshot.data as unknown as T, newSnapshot.sourceMap);
          cb?.(newSnapshot);
        }
      });
    };

    result = useAsyncData<SanityQueryResponse<T | null>, E>(
      queryKey,
      async () => {
        pushDebug({ query, params: params || {}, queryKey });
        if (!import.meta.server) {
          console.warn(
            '[useWwwSanityQuery] Using sanity.queryStore on the client without a fetcher is not supported'
          );
          return { data: null };
        }

        const client = (sanity.queryStore!.unstable__serverClient.instance ||
          sanity.client) as unknown as SanityClient;

        const { result, resultSourceMap: sourceMap } = await client.fetch<T>(query, params || {}, {
          perspective,
          filterResponse: false,
          resultSourceMap: 'withKeyArraySelector',
        });
        const data = stegaClean(result) as T | null;
        const { extractRefs, getSanityQueryAndParamsFromRefs, storeRefs } = useRefsStore();
        const firstExtractedRefs = extractRefs(data);
        if (!firstExtractedRefs.length) {
          return sourceMap ? { data, sourceMap } : { data };
        }
        const { query: firstQuery, params: firstParams } =
          getSanityQueryAndParamsFromRefs(firstExtractedRefs);
        const firstResp = await client.fetch<T>(firstQuery, firstParams, {
          perspective,
          filterResponse: false,
          resultSourceMap: 'withKeyArraySelector',
        });
        storeRefs({
          refs: firstExtractedRefs,
          data: stegaClean(firstResp.result),
        });
        const secondExtractedRefs = extractRefs(firstResp.result);
        if (!secondExtractedRefs.length) {
          return sourceMap ? { data, sourceMap } : { data };
        }
        const { query: secondQuery, params: secondParams } =
          getSanityQueryAndParamsFromRefs(secondExtractedRefs);
        const secondResp = await client.fetch<T>(secondQuery, secondParams, {
          perspective,
          filterResponse: false,
          resultSourceMap: 'withKeyArraySelector',
        });
        storeRefs({
          refs: secondExtractedRefs,
          data: stegaClean(secondResp.result),
        });

        return sourceMap ? { data, sourceMap } : { data };
      },
      options
    ) as AsyncData<SanityQueryResponse<T | null>, E>;

    // On the client, setup the fetcher
    if (import.meta.client) {
      setupFetcher();
      if (params) watch(params, () => setupFetcher());
    }

    onScopeDispose(unsubscribe);
  }

  return Object.assign(
    new Promise((resolve) => {
      result.then((value) => {
        AssertThat.isNullOrUndefined(result.error.value, `useAsyncData: ${result.error.value}`);

        if (!value.data || !value.data.value || !value.data.value.data) {
          const key = (Math.random() * Number.MAX_SAFE_INTEGER).toString(36).substring(0, 6);
          pushClientErr({
            msg: `${key} - in resolve assign key`,
            query,
            params: params || {},
            queryKey,
            debugPath,
          });
        }

        updateRefs(value.data.value.data, value.data.value.sourceMap);
        resolve({
          ...result,
          data,
          sourceMap,
          encodeDataAttribute,
        });
      });
    }),
    { ...result, data, sourceMap, encodeDataAttribute }
  ) as AsyncSanityData<T | null>;
};

const localizePathname = (pathname: string): string => {
  const lang = new RegExp('^/(fr|de|es|it|ja|ko|pt|zh)/.*?$').exec(pathname);
  if (lang && lang[1]) {
    return pathname.replace(lang[1], 'en');
  }
  return pathname;
};

export const useWwwSanityQueryNonNull = async <T = unknown, E = Error>(
  query: string,
  _params?: QueryParams,
  _options: UseSanityQueryOptions<SanityQueryResponse<T | null>> & WwwExtraOptions = {}
): Promise<AsyncSanityData<T>> => {
  const result = await useWwwSanityQuery<T, E>(query, _params, _options);
  AssertThat.getNonNullRefOrThrow(result.data, 'Expected data to be non-null from query: ' + query);
  return result as AsyncSanityData<T> & { data: Ref<T> };
};
